import { html, LitElement, nothing, type PropertyValues, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Orientation } from '../../../_ui-core_/base/package/api/package/gaming/gaming-domain';
import { I18n } from '../../../_ui-core_/base/package/services/package/i18n/i18n.de';
import { handleKeyboardTrigger } from '../../../_ui-core_/base/package/util/a11y';
import { getDeviceOrientation } from '../../../_ui-core_/base/package/util/device';
import { dispatchCustomEvent } from '../../../_ui-core_/base/package/util/event';
import { NavBarIconName } from '../../../_ui-core_/components/nav-bar/ui-nav-bar-icon';
import {
  openGamification,
  showGamificationButton,
} from '../../../_ui-core_/mod-incentive/helper/gamification-launcher';
import type {
  ContributionTarget,
  ContributionTournamentTarget,
  MissionState,
  TournamentState,
} from '../../../_ui-core_/mod-incentive/types';
import App from '../../../app';
import { TrackableEventAction } from '../../../app/package/base/service/tracking/tracking-service';
import '../view/ui-game-view';
import { resolveAndRasterizeGameAssetUrl } from '../../../_ui-core_/base/package/util/game-asset-url';
import styles from './game-page-mobile.css?inline';
import '../../../_ui-core_/components/icons/ui-info-icon';
import '../../../_ui-core_/components/incentive/mission-bar/mission-bar';
import '../../../_ui-core_/components/incentive/tournament-bar/tournament-bar';
import '../../../_ui-core_/components/layout/ui-layout-bar/ui-layout-bar';
import '../../../_ui-core_/components/nav-bar/ui-gamification-icon';
import '../../../_ui-core_/components/ui-header/ui-header-icon-user';
import '../../../_ui-core_/components/ui-lugas-session-timer/ui-lugas-session-timer';
import '../../../_ui-core_/components/ui-panic-button/ui-panic-button';

const CName = 'game-page-mobile';

/**
 * @fires showDiscovery - When the discovery icon is clicked
 * @fires showPanicInfo - When the panic info icon is clicked
 * @fires showQuickDeposit - When the deposit icon is clicked
 */
@customElement(CName)
export class GamePageMobile extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @property({ type: Boolean }) isNativeIos = false;
  @property({ type: Boolean }) forReal: boolean;
  @property({ type: String }) gameId: string;
  @property({ type: String }) gameName: string;
  @property({ type: String }) gameSlug: string;
  @property({ type: Boolean }) renderFavButton = false;
  @property({ type: Object }) incentiveMissionTarget: ContributionTarget | null;
  @property({ type: Object }) incentiveMissionState?: MissionState;
  @property({ type: Object }) incentiveTournamentTarget: ContributionTournamentTarget | null;
  @property({ type: Object }) incentiveTournamentState?: TournamentState;

  @state() private _layout: 'portrait' | 'landscape' = getDeviceOrientation();
  @state() private _shouldRenderGamificationIcon = false;
  @state() private _isSidebar = false;
  @state() private _gameAssetBackground = 'transparent';

  private _handleOrientationChange = (ev: Event) => {
    const target = ev.target as ScreenOrientation;
    const type = target.type;
    window.$app.logger.log(`📱 orientation is now ${type}`);
    this._checkDeviceOrientation(type);
  };

  private _handleResize = () => {
    this._isSidebar = this._computeSidebar();
  };

  private _abortController: AbortController | null = null;

  isProviderEveryMatrix() {
    return this.gameId.startsWith('em');
  }

  connectedCallback() {
    super.connectedCallback();
    this._abortController = new AbortController();
    // Listen for the orientation change to resolve the Safari Mobile issue (IGM-3184).
    screen.orientation.addEventListener('change', this._handleOrientationChange);
    window.addEventListener('resize', this._handleResize);
    this._layout = getDeviceOrientation();
    this._isSidebar = this._computeSidebar();
    const signal = this._abortController.signal;
    if (!signal.aborted) {
      this._shouldRenderGamificationIcon = showGamificationButton();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    screen.orientation.removeEventListener('change', this._handleOrientationChange);
    window.removeEventListener('resize', this._handleResize);
    this._abortController?.abort();
    this._abortController = null;
  }

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    const signal = this._abortController?.signal;

    App.instance.checkPlay(this.forReal, this.gameId).then((isStartupOK) => {
      if (signal?.aborted) return;
      if (!isStartupOK) {
        window.$app.logger.log('startup check failed => redirect to home');
        App.instance.router.navigateToHome();
        return;
      }
      if (changedProperties.has('gameId')) this._checkOrientation(this.gameId);
    });

    if (changedProperties.has('gameId')) {
      this._fetchGameAsset(this.gameId);
    }
  }

  render() {
    return html`
      <main class="mobile-game-page ${this._layout}">
        <div class="lugas-timer">
          <ui-lugas-session-timer></ui-lugas-session-timer>
        </div>
        <div class="panic-button">
          <ui-panic-button
            .secondary="${false}"
            .pressDurationS=${3}
            .text=${window.$app.$t.get(I18n.gamePage.panicButton)}
          ></ui-panic-button>
          <ui-info-icon @click=${this._handlePanicInfoClick}></ui-info-icon>
          ${this._renderAccountButton()}
        </div>
        <ui-game-view
          class="mobile-game-selection"
          .forReal=${this.forReal}
          .gameId=${this.gameId}
          .gameName=${this.gameName}
          .gameSlug=${this.gameSlug}
          .isNativeIos=${this.isNativeIos}
          .incentiveMission=${this.incentiveMissionTarget}
        ></ui-game-view>
        ${this._renderProgressBar()}
        <div class="toolbar">
          <ui-layout-bar class=${this._isSidebar ? 'sidebar' : ''}>
            <nav slot="center">
              <ui-nav-bar-icon
                .icon=${NavBarIconName.DISCOVER}
                @click=${() => this._handleGameBottomBarClick(NavBarIconName.DISCOVER)}
                label=${App.instance.appSettings.isSHCasino ? window.$app.$t.get(I18n.navigation.navbar.leave) : window.$app.$t.get(I18n.navigation.navbar.discover)}
              ></ui-nav-bar-icon>
              ${
                this._shouldRenderGamificationIcon
                  ? html`<ui-gamification-icon
                      tabindex="0"
                      role="button"
                      aria-label=${window.$app.$t.get(I18n.navigation.navbar.missions)}
                      @click=${this._handleMissionClick}
                      @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._handleMissionClick.bind(this))}
                      ?isActive=${true}
                    ></ui-gamification-icon>`
                  : nothing
              }
              <ui-nav-bar-icon
                .icon=${NavBarIconName.DEPOSIT}
                @click=${() => this._handleGameBottomBarClick(NavBarIconName.DEPOSIT)}
                label=${window.$app.$t.get(I18n.navigation.navbar.deposit)}
              ></ui-nav-bar-icon>
            </nav>
          </ui-layout-bar>
        </div>
      </main>
    `;
  }

  private _renderAccountButton() {
    return html`
        <ui-header-icon-user
          @click=${() => this._goToAccount()}
          @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._goToAccount)}
          aria-label=${window.$app.$t.get(I18n.base.account)}
          class="account-button"
          role="button"
          tabindex="0"
        ></ui-header-icon-user>
      `;
  }

  private _renderProgressBar() {
    if (this.incentiveMissionTarget) {
      return html`
        <incentive-mission-bar
          .bundleId=${this.incentiveMissionTarget.bundleId}
          .expirationDate=${this.incentiveMissionTarget.endDate}
          .reward=${this.incentiveMissionTarget.reward}
          .progress=${this.incentiveMissionState?.progress}
          .remaining=${this.incentiveMissionState?.remaining}
          .isExpired=${this.incentiveMissionState?.isExpired}
          class="mission-bar"
        ></incentive-mission-bar>`;
    }

    if (this.incentiveTournamentTarget && this.incentiveTournamentState?.hasStarted) {
      return html`
        <incentive-tournament-bar
          .tournamentId=${this.incentiveTournamentTarget.tournamentId}
          .expirationDate=${this.incentiveTournamentTarget.endDate}
          .position=${this.incentiveTournamentState?.userPosition}
          .points=${this.incentiveTournamentState?.points}
          .joinedAmount=${this.incentiveTournamentState?.joinedAmount}
          .isEnded=${this.incentiveTournamentState?.hasEnded}
          .areResultsReady=${this.incentiveTournamentState?.areResultsReady}
          class="mission-bar"
        ></incentive-tournament-bar>`;
    }

    return nothing;
  }

  private _goToAccount() {
    App.instance.router.navigateToAccount();
  }

  private _checkDeviceOrientation(
    orientationType: 'portrait-primary' | 'landscape-primary' | 'portrait-secondary' | 'landscape-secondary',
  ) {
    const orientation = orientationType.startsWith('portrait') ? 'portrait' : 'landscape';
    window.$app.logger.log('📱 device orientation:', orientation);
    if (orientation !== this._layout) {
      this._layout = orientation;
    }
    this._isSidebar = this._computeSidebar();
  }

  private _handleMissionClick() {
    window.$app.track.menuNavigation({
      eventAction: TrackableEventAction.GAMIFICATION_ICON,
      navMode: App.instance.router.isGameRoute() ? 'gameMode' : 'normalMode',
    });
    openGamification(true);
  }

  private _checkOrientation(gameId: string): void {
    const signal = this._abortController?.signal;
    window.$app.logger.log(`checking orientation for game: ${gameId}`);
    App.instance.content.getGame(gameId).then((game) => {
      if (signal?.aborted) return;
      if (game?.exclude?.orientations?.includes(Orientation.LANDSCAPE)) {
        App.instance.native.sendLandscapeEnable(false);
      } else {
        App.instance.native.sendLandscapeEnable(true);
      }
    });
  }

  private _handlePanicInfoClick() {
    dispatchCustomEvent(this, 'showPanicInfo');
  }

  private _fetchGameAsset(gameId: string) {
    const signal = this._abortController?.signal;
    App.instance.content
      .getGameInfo(gameId)
      .then(async (gameInfo) => {
        if (signal?.aborted) return;
        const img = gameInfo.img ?? null;
        this._gameAssetBackground = img ? await resolveAndRasterizeGameAssetUrl(img) : 'transparent';
        if (signal?.aborted) return;
        this.style.setProperty('--_gameAsset', this._gameAssetBackground);
      })
      .catch((err) => {
        if (signal?.aborted) return;
        window.$app.logger.warn(`Error fetching game info '${gameId}'`, err);
      });
  }

  private _handleGameBottomBarClick(name: NavBarIconName) {
    window.$app.track.menuNavigation({
      eventAction: name,
      navMode: 'gameMode',
    });
    switch (name) {
      case NavBarIconName.DISCOVER:
        dispatchCustomEvent(this, 'showDiscovery');
        break;
      case NavBarIconName.DEPOSIT:
        dispatchCustomEvent(this, 'showQuickDeposit');
        break;
    }
  }

  private _computeSidebar(): boolean {
    // Must stay in sync with the CSS @media (max-aspect-ratio: 1/1) rule
    // that forces portrait grid layout when viewport is portrait-like.
    const ratio = window.innerWidth / window.innerHeight;
    if (window.$app.device.isMobileDevice) {
      return this._layout === 'landscape' && ratio >= 1;
    }
    return ratio >= 1;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: GamePageMobile;
  }
}
